import React from 'react';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * SpecialistsPage component for Healthcare Claims Specialists
 * Provides FAQ and guidance for claims specialists using AI validation tools
 */
const SpecialistsPage = () => {
  // FAQ data structure
  const faqs = [
    {
      question: "How does the AI assist in claims validation?",
      answer: "Our AI system pre-screens claims and highlights potential issues or discrepancies. It analyzes coding patterns, documentation completeness, and compliance requirements, presenting you with focused recommendations that need your expert review and validation."
    },
    {
      question: "What types of claims issues can the AI detect?",
      answer: "The system identifies multiple areas including: incorrect coding combinations, missing documentation, non-covered services, duplicate claims, and potential compliance risks. It uses historical data and current guidelines to flag unusual patterns that warrant specialist review."
    },
    {
      question: "How do I review AI-suggested updates?",
      answer: "Each suggested update appears in your work queue with a confidence score and supporting evidence. You can quickly view the original claim, relevant documentation, and the AI's reasoning. Accept, modify, or reject suggestions based on your expertise."
    },
    {
      question: "What if I disagree with an AI suggestion?",
      answer: "Your expertise is paramount. If you disagree with an AI suggestion, you can reject it and provide a reason. This feedback helps improve the AI's accuracy over time. All final decisions require your professional validation."
    },
    {
      question: "How does the system handle complex claims?",
      answer: "For complex claims, the AI provides detailed analysis breakdowns and references relevant policies or guidelines. You maintain full control to review each component and make informed decisions based on your expertise and current regulations."
    },
    {
      question: "Can I track my validation decisions?",
      answer: "Yes, the system maintains a comprehensive audit trail of all your reviews and decisions. You can access historical data, track productivity metrics, and generate reports on validation patterns and outcomes."
    }
  ];

  return (
    <div className="specialists-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Claims Specialist <span style={{ color: '#FFC000' }}>Solutions</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Empowering healthcare claims specialists with AI-assisted validation tools
              while maintaining professional oversight and control.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - FAQ Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Key Benefits
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>⚡</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Enhanced Productivity
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Process claims faster with AI-powered pre-screening and validation assistance.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>🎯</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Improved Accuracy
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Reduce errors with intelligent validation checks and compliance monitoring.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="text-4xl mb-4" style={{ color: '#FFC000' }}>📊</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Better Insights
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Gain valuable insights from detailed analytics and tracking capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to streamline your claims validation process?"
        description="Experience how our AI-assisted platform can enhance your workflow while maintaining professional control."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Us"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default SpecialistsPage;

